import { Select } from '@mds/web-ui-select';
import { ListItem, ListItemText } from '@mds/web-ui-list';
import { Switch } from '@mds/web-ui-switch';
import { Tr, Td, TableCellText } from '@mds/web-ui-table';
import { Text } from '@mds/web-ui-text';
import React, { useState, useEffect } from 'react';
import { IconClose } from '@mds/web-ui-icons';
import { Link } from '@mds/web-ui-link';
import type { ContactOption } from './ContactsNotificationPreference';
import styles from './NotificationTable.module.scss';
import type { Role } from '../../models/Role';
import {joinStyles, toCamelCase} from '../../../../utils/helper';
import {saveRoleAssignment} from '../../services/role-assignment-service';
import {useMutation} from '@tanstack/react-query';
import Loader from '../Loader/Loader';
import type {SingleValue} from 'react-select';
import type {LoanDataFields} from '../../models/LoanDataFields';

interface RowProps {
  roleData:any;
  options: ContactOption[];
  role?: Role;
  handleRemoveAlt?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  selectedContacts: Set<string>; // New prop to track selected contacts
  onSelectContact: (roleID: string, contactID: string, previousContactID?: string) => void; // Updated prop to handle contact selection
  selectedLoanType:any;
  loanId?:string|null;
  loanDataFields?: LoanDataFields
}

const ConfigRuleRow: React.FC<RowProps> = ({
                                             roleData,
                                             options,
                                             role,
                                             handleRemoveAlt,
                                             selectedContacts,
                                             onSelectContact,
                                             selectedLoanType,
                                             loanId,
                                             loanDataFields
                                           }) => {
  const [selected, setSelected] = useState<ContactOption| null>(null);
  const [isEmailPreference, setIsEmailPreference] = useState(false);
  const { roleID, primary } = role || {};

  const postRoleAssignment = useMutation({
    mutationFn: (payload) => saveRoleAssignment(payload),
  });

  useEffect(() => {
    if(loanDataFields?.emailPreferences){
      const roleNameToCamelCase = toCamelCase(role?.roleName);
      // @ts-ignore
      const isEmailPref = loanDataFields?.emailPreferences?.[roleNameToCamelCase] === 'N' ?false:true ;
      setIsEmailPreference(isEmailPref)
    }
  }, [loanDataFields]);

  const handleSelectItemClick = (contact: ContactOption) => {
    const previousContactID = selected?.id;
    setSelected(contact);
    setIsEmailPreference(false)
    if (role?.roleID) {
      onSelectContact(role.roleID, contact.id, previousContactID);
    }
    const getRoleId = roleData.find((rol:any)=>rol.roleName ===role?.roleName)
    const payload = {
      "loanId": loanId,
      "loanAssociates": [
        {
          "id": getRoleId?.id,
          "roleName": role?.roleName,
          "loanAssociateType": "User",
          "email": contact?.email,
          "user": {
            "entityType": "User",
            "entityId": contact?.id,
            "email": contact?.email
          },
          "emailPreference": primary ? true: false
        }
      ]
    };
    handlePostSaveRoleAssignment(payload)
  };
  const handlePostSaveRoleAssignment=(payload:any)=>{

    postRoleAssignment.mutate(payload)
  }

  const handleSwitchToggle = (rowEmail:any) => {
    setIsEmailPreference((oldState) => {
      const getRoleId = roleData.find((rol:any)=>rol.roleName ===role?.roleName)
      const payload = {
        "loanId": loanId,
        "loanAssociates": [
          {
            "id": getRoleId?.id,
            "roleName": role?.roleName,
            "loanAssociateType": "User",
            "email": rowEmail ?? role?.user?.email ?? selected?.email,
            "user": {
              "entityType": "User",
              "entityId": selected?.id?? role?.user?.entityId,
              "email": rowEmail ?? role?.user?.email ?? selected?.email
            },
            "emailPreference": primary ? true: !oldState
          }
        ]
      };
      handlePostSaveRoleAssignment(payload)
      return !oldState
    });


  }
  const removeUser=()=>{
    const payload = {
      "loanId": loanId,
      "loanAssociates": [
        {
          "id": role?.id,
          "roleName": role?.roleName,
          "loanAssociateType":null,
          "user": null,
          "emailPreference":false
        }
      ]
    };
    handlePostSaveRoleAssignment(payload)
  }
  const filteredUserByRole = options.filter((opt)=> {
    const listOfRoles= opt?.role?.map((op:SingleValue<Role>)=> op?.entityName);
    return listOfRoles?.includes(role?.roleName) && !opt?.disabledLogin
  })
  const filterPersistedUser = options.find(contact=> contact.id === role?.user?.entityId)

  const isRadioBtnDisable = ()=>{
    if(primary){
      return true
    }
    else if(selected){
      return false
    }else if (role?.user ){
      return false
    }else{
      return true
    }
  }
  const getSelectedRoleUserName = filteredUserByRole?.find(contact=> contact.id === role?.user?.entityId)?.displayName
  filteredUserByRole?.sort((a,b)=> a.displayName.localeCompare(b.displayName));
  return (
    <>
      {postRoleAssignment.isPending && <Loader />}
      <Tr key={roleID} id={roleID}>
        <Td scope="row" className={styles.middle}>
          <TableCellText
            text={
              <Text textStyle="bodyMediumHeavier">
                {primary ? 'Primary' : 'Alternate'}
              </Text>
            }
          />
        </Td>
        <Td
          text={
            <Select
              data-testid="contact-select"
              defaultValue={getSelectedRoleUserName ?? undefined}
              placeholder="Choose contact"
            >
              {filteredUserByRole.map((contact) => (
                <ListItem
                  key={contact.displayName}
                  displayValue={contact.displayName}
                  value={contact.displayName}
                  id={contact.displayName}
                  onClick={() => handleSelectItemClick(contact)}
                >
                  <ListItemText text={contact.displayName} />
                </ListItem>
              ))}
            </Select>
          }
        />
        <Td className={styles.middle}>{selected?.email ?? filterPersistedUser?.email}</Td>
        <Td className={styles.middle}>{selected?.phone ?? filterPersistedUser?.phone}</Td>
        <Td className={joinStyles([styles.middle, styles.center])} onClick={()=>handleSwitchToggle(selected?.email ?? filterPersistedUser?.email)}>
          {/*<Switch onChangeCommit={(state)=>handleSwitchToggle(state,selected?.email ?? filterPersistedUser?.email)} checked={isEmailPreference} disabled={isRadioBtnDisable() } />*/}
          <Switch
            size="small"
            aria-checked={isEmailPreference}
            checked={isEmailPreference}
            id="uniqueId"
            aria-describedby="OnOff"
            aria-labelledby="uniqueSpanId"
            disabled={isRadioBtnDisable()}
            onChange={(state) => handleSwitchToggle(selected?.email ?? filterPersistedUser?.email)}
            // style={{ width: '44px' }}
          />
        </Td>
        <Td className={styles.middle}>
          {!primary && (
            <Link id={roleID} onClick={(e)=>{
              if (handleRemoveAlt) {
                handleRemoveAlt(e);
                removeUser();
              }
            }}>
              <IconClose
                aria-label="close"
                role="img"
                size="large"
                className={styles.middle}
                color="regular"
              />
            </Link>
          )}
        </Td>
      </Tr>
    </>
  );
};

export default ConfigRuleRow;
